import type { CanvasItem } from "@bedrock/domain";
import { boundsForItems, fitBounds, worldToScreen, type CanvasRect, type Viewport } from "@bedrock/canvas";

const STACK_FOCUS_CLICK_SLOP = 5;

export function viewportForStackFocus(
  viewport: Viewport,
  stack: CanvasRect,
  children: CanvasRect[],
  viewportWidth: number,
  viewportHeight: number,
  padding: number
): Viewport | null {
  if (children.length === 0) return null;

  const bounds = boundsForItems([stack, ...children]);
  const topLeft = worldToScreen({ x: bounds.minX, y: bounds.minY }, viewport);
  const bottomRight = worldToScreen({ x: bounds.maxX, y: bounds.maxY }, viewport);
  if (
    topLeft.x >= padding
    && topLeft.y >= padding
    && bottomRight.x <= viewportWidth - padding
    && bottomRight.y <= viewportHeight - padding
  ) return null;

  const fitted = fitBounds(bounds, viewportWidth, viewportHeight, padding);
  const scale = Math.min(viewport.scale, fitted.scale);
  return {
    scale,
    x: viewportWidth / 2 - ((bounds.minX + bounds.maxX) / 2) * scale,
    y: viewportHeight / 2 - ((bounds.minY + bounds.maxY) / 2) * scale
  };
}

export function isStackFocusTarget(
  focusedStackId: string,
  targetItemId: string | null,
  items: Pick<CanvasItem, "id" | "parentStackId">[]
): boolean {
  if (!targetItemId) return false;
  if (targetItemId === focusedStackId) return true;
  return items.some((item) => item.id === targetItemId && item.parentStackId === focusedStackId);
}

export function registerStackFocusDismissal(
  focusedStackId: string,
  getItems: () => Pick<CanvasItem, "id" | "parentStackId">[],
  onDismiss: () => void
): () => void {
  const outsidePresses = new Map<number, { x: number; y: number; moved: boolean }>();
  const closeOnEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape" && !event.isComposing) onDismiss();
  };
  const closeOnOutsidePointerDown = (event: PointerEvent) => {
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-canvas-item-id]") : null;
    if (isStackFocusTarget(focusedStackId, target?.dataset["canvasItemId"] ?? null, getItems())) return;
    outsidePresses.set(event.pointerId, { x: event.clientX, y: event.clientY, moved: false });
  };
  const trackOutsidePointerMove = (event: PointerEvent) => {
    const press = outsidePresses.get(event.pointerId);
    if (!press || press.moved) return;
    if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > STACK_FOCUS_CLICK_SLOP) {
      press.moved = true;
    }
  };
  const finishOutsidePointer = (event: PointerEvent) => {
    const press = outsidePresses.get(event.pointerId);
    outsidePresses.delete(event.pointerId);
    if (event.type === "pointerup" && press && !press.moved) onDismiss();
  };

  window.addEventListener("keydown", closeOnEscape, { capture: true });
  document.addEventListener("pointerdown", closeOnOutsidePointerDown, { capture: true });
  document.addEventListener("pointermove", trackOutsidePointerMove, { capture: true });
  document.addEventListener("pointerup", finishOutsidePointer, { capture: true });
  document.addEventListener("pointercancel", finishOutsidePointer, { capture: true });
  return () => {
    window.removeEventListener("keydown", closeOnEscape, { capture: true });
    document.removeEventListener("pointerdown", closeOnOutsidePointerDown, { capture: true });
    document.removeEventListener("pointermove", trackOutsidePointerMove, { capture: true });
    document.removeEventListener("pointerup", finishOutsidePointer, { capture: true });
    document.removeEventListener("pointercancel", finishOutsidePointer, { capture: true });
  };
}
